import { classNames } from 'primereact/utils'
import { useState } from 'react'
import { SelectOption } from '../../../types/types'
import MonitorsChart, { ChartViewSettings } from './MonitorsChart'
import type { RunCase } from './types'
import TrendsChart, { AverageMode } from './TrendsChart'

export type ChartContainerProps = {
  chartKey: string
  xIndex: number
  yIndex: number
  defaultXAxis?: string
  defaultYAxis?: string | string[]
  defaultTrendsXAxis?: string
  defaultTrendsYAxis?: string | string[]
  defaultYScale?: 'linear' | 'log'
  defaultViewSettings?: ChartViewSettings
  defaultAverageMode?: AverageMode
  xMonitors: SelectOption[]
  yMonitors: SelectOption[]
  selectedCases: RunCase[]
  showRollingAverage?: boolean
  chartDataType: 'monitors' | 'trends'
  defaultWindowSize?: number
  trendsData?: Record<string, any>[]
  runAverageData?: Record<string, any>[]
  onXAxisChange?: (row: number, col: number, xAxis: string) => void
  onYAxisChange?: (row: number, col: number, yAxis: string[]) => void
  onTrendsXAxisChange?: (row: number, col: number, xAxis: string) => void
  onTrendsYAxisChange?: (row: number, col: number, yAxis: string[]) => void
  onAverageModeChange?: (row: number, col: number, averageMode: AverageMode) => void
  onYScaleChange?: (row: number, col: number, yScale: 'linear' | 'log') => void
  onViewSettingsChange?: (row: number, col: number, viewSettings: ChartViewSettings) => void
  onAddChartRight: (row: number, col: number) => void
  onAddChartBelow: (row: number, col: number) => void
  onRemoveChart: (row: number, col: number) => void
  onPlotingStatesChange: (row: number, col: number, plotting: boolean) => void
  onRollingAverageChange: (row: number, col: number, showRollingAverage: boolean) => void
  onRollingAverageWindowSizeChange: (row: number, col: number, windowSize: number) => void
  onChartDataTypeChange: (row: number, col: number, chartDataType: 'monitors' | 'trends') => void
  onEnlargeChart?: (row: number, col: number) => void
  isEnlarged?: boolean
}

const ChartContainer = ({
  chartKey,
  xIndex,
  yIndex,
  defaultXAxis,
  defaultYAxis,
  defaultTrendsXAxis,
  defaultTrendsYAxis,
  defaultYScale = 'linear',
  defaultViewSettings,
  defaultAverageMode = 'filtered',
  xMonitors,
  yMonitors,
  selectedCases,
  showRollingAverage = false,
  chartDataType = 'monitors',
  defaultWindowSize = 50,
  trendsData,
  runAverageData,
  onXAxisChange,
  onYAxisChange,
  onTrendsXAxisChange,
  onTrendsYAxisChange,
  onAverageModeChange,
  onYScaleChange,
  onViewSettingsChange,
  onAddChartRight,
  onAddChartBelow,
  onRemoveChart,
  onPlotingStatesChange,
  onEnlargeChart,
  onRollingAverageChange,
  onRollingAverageWindowSizeChange,
  onChartDataTypeChange,
  isEnlarged = false
}: ChartContainerProps) => {
  const [chartDataTypeState, setChartDataTypeState] = useState<'monitors' | 'trends'>(chartDataType)
  return (
    <div className="p-2 relative h-full flex flex-col" style={{ minHeight: '300px' }}>
      <div className="flex items-center justify-between z-20">
        <div className="flex items-center space-x-1">
          <button
            className="text-base flex items-center justify-center p-1 text-neutral-70 dark:text-white"
            onClick={() => {
              if (isEnlarged) {
                onRemoveChart(yIndex, xIndex)
              } else {
                onEnlargeChart && onEnlargeChart(yIndex, xIndex)
              }
            }}
            title={isEnlarged ? 'Reduce' : 'Expand'}>
            <i
              className={`pi text-[0.75rem] ${isEnlarged ? 'pi-window-minimize' : 'pi-expand'} `}></i>
          </button>
          <button
            className={classNames('p-1', {
              'text-gm-blue dark:text-light-blue font-semibold': chartDataTypeState === 'monitors',
              'text-gray-400 dark:text-neutral-10': chartDataTypeState === 'trends'
            })}
            onClick={() => {
              if (chartDataTypeState === 'monitors') {
                return
              }
              const newType = 'monitors'
              setChartDataTypeState(newType)
              onChartDataTypeChange(yIndex, xIndex, newType)
            }}>
            Monitors
          </button>
          <button
            className={classNames('p-1', {
              'text-gm-blue dark:text-light-blue font-semibold': chartDataTypeState === 'trends',
              'text-gray-400 dark:text-neutral-10': chartDataTypeState === 'monitors'
            })}
            onClick={() => {
              if (chartDataTypeState === 'trends') {
                return
              }
              const newType = 'trends'
              setChartDataTypeState(newType)
              onChartDataTypeChange(yIndex, xIndex, newType)
            }}>
            Trends
          </button>
        </div>
        <div className="flex items-center space-x-2">
          <button
            className="flex items-center gap-1 px-[6px] py-[3px] rounded text-[12px] bg-[#f1f5f9] hover:bg-[#e2e8f0] text-[#45556c] hover:text-[#08498a] transition-colors border border-[#ddd] dark:bg-neutral-70 dark:text-neutral-10 dark:border-neutral-20 dark:hover:bg-neutral-60"
            onClick={() => onAddChartRight(yIndex, xIndex)}>
            <i className="pi pi-plus text-[0.6rem]"></i>
            <i className="pi pi-arrow-right text-[0.6rem]"></i>
          </button>
          <button
            className="flex items-center gap-1 px-[6px] py-[3px] rounded text-[12px] bg-[#f1f5f9] hover:bg-[#e2e8f0] text-[#45556c] hover:text-[#08498a] transition-colors border border-[#ddd] dark:bg-neutral-70 dark:text-neutral-10 dark:border-neutral-20 dark:hover:bg-neutral-60"
            onClick={() => onAddChartBelow(yIndex, xIndex)}>
            <i className="pi pi-plus text-[0.6rem]"></i>
            <i className="pi pi-arrow-down text-[0.6rem]"></i>
          </button>
          <button
            className="text-lg flex items-center justify-center text-neutral-70 dark:text-white"
            onClick={() => onRemoveChart(yIndex, xIndex)}>
            <i className="pi pi-times text-[0.75rem]"></i>
          </button>
        </div>
      </div>
      <div className="flex-1 min-h-0 h-full">
        {chartDataTypeState === 'monitors' && (
          <MonitorsChart
            chartKey={chartKey}
            xIndex={xIndex}
            yIndex={yIndex}
            defaultXAxis={defaultXAxis}
            defaultYAxis={defaultYAxis}
            defaultYScale={defaultYScale}
            defaultViewSettings={defaultViewSettings}
            xMonitors={xMonitors}
            yMonitors={yMonitors}
            selectedCases={selectedCases}
            showRollingAverage={showRollingAverage}
            defaultWindowSize={defaultWindowSize}
            onXAxisChange={onXAxisChange}
            onYAxisChange={onYAxisChange}
            onYScaleChange={onYScaleChange}
            onViewSettingsChange={onViewSettingsChange}
            onPlotingStatesChange={onPlotingStatesChange}
            onEnlargeChart={onEnlargeChart}
            onRollingAverageChange={onRollingAverageChange}
            onRollingAverageWindowSizeChange={onRollingAverageWindowSizeChange}
          />
        )}

        {chartDataTypeState === 'trends' && (
          <TrendsChart
            xIndex={xIndex}
            yIndex={yIndex}
            defaultXAxis={defaultTrendsXAxis}
            defaultYAxis={defaultTrendsYAxis}
            defaultAverageMode={defaultAverageMode}
            onXAxisChange={onTrendsXAxisChange}
            onYAxisChange={onTrendsYAxisChange}
            onAverageModeChange={onAverageModeChange}
            headlineData={trendsData}
            runAverageData={runAverageData}
            selectedCases={selectedCases}
          />
        )}
      </div>
    </div>
  )
}

export default ChartContainer
